// components/GTM.js - Enhanced version with UTMs from React Context
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from 'context/ConsentContext';
import { useTranslation } from 'react-i18next';

// Module-level variables for purchase deduplication
let purchaseEventId = null;
let purchaseEventTimestamp = 0;

// ============================================================
// Direct helper to get UTMs from sessionStorage
// ============================================================
const getUTMsFromSessionStorage = () => {
  if (typeof sessionStorage === 'undefined') return {};
  
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'gclid'];
  const utms = {};
  
  keys.forEach(key => {
    const value = sessionStorage.getItem(key);
    if (value && value !== '' && value !== '(direct)' && value !== '(none)') {
      utms[key] = value;
    }
  });
  
  return utms;
};

// Helper to check if analytics consent is granted
const hasAnalyticsConsent = () => {
  try {
    const consent = JSON.parse(localStorage.getItem('gtm_consent') || '{}');
    return consent.analytics === true;
  } catch {
    return false;
  }
};

// Helper to get UTMs from global React Context reference
const getUTMsFromContext = (includeGclid = false) => {
  try {
    if (typeof window !== 'undefined' && window.__utmContext) {
      if (includeGclid && window.__utmContext.getUtms) {
        return window.__utmContext.getUtms(true);
      } else if (window.__utmContext.getUtms) {
        return window.__utmContext.getUtms(false);
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[GTM] Error getting UTMs from context:', error);
    }
  }
  return {};
};

// ============================================================
// Get UTMs for debug - gclid is NEVER included in debug events
// ============================================================
const getUTMsForDebugFromStorage = () => {
  const utms = getUTMsFromSessionStorage();
  
  // gclid is NEVER included in debug events
  if (utms.gclid) {
    delete utms.gclid;
  }
  
  return utms;
};

// ============================================================
// Get UTMs for analytics - gclid included ONLY with consent
// ============================================================
const getUTMsForAnalyticsFromStorage = () => {
  const consentGranted = hasAnalyticsConsent();
  const utms = getUTMsFromSessionStorage();
  
  // Only include gclid if consent is granted
  if (!consentGranted && utms.gclid) {
    delete utms.gclid;
  }
  
  return utms;
};

// Helper: Get or create session ID
const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem('gtm_functional_session_id');
  if (!sessionId) {
    sessionId = 'func_sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('gtm_functional_session_id', sessionId);
  }
  return sessionId;
};

// Helper: Hash email
const hashEmail = (email) => {
  if (!email) return null;
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
};

// Helper: Get current consent state
const getConsentState = () => {
  try {
    const consent = localStorage.getItem('gtm_consent');
    return consent ? JSON.parse(consent) : { analytics: false, functional: true };
  } catch {
    return { analytics: false, functional: true };
  }
};

const GTM = () => {
  const location = useLocation();
  const { hasConsent } = useConsent();
    const { i18n } = useTranslation("global"); // ⬅️ GET i18n instance


  // ============================================================
  // Push UTMs to dataLayer for debug visibility ONLY
  // These events are NOT sent to GA4 - debug_only: true
  // gclid is NEVER included in debug events
  // ============================================================
  useEffect(() => {
    const pushUTMsForDebug = () => {
      const utms = getUTMsForDebugFromStorage();
      
      if (window.dataLayer && Object.keys(utms).length > 0) {
        const eventData = {
          'event': 'utm_captured_debug',
          ...utms,
          'timestamp': new Date().toISOString(),
          'debug_only': true,
          'consent_required': false,
          // 'gclid_included': false 
        };
        
        window.dataLayer.push(eventData);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] UTMs pushed for debug (gclid NEVER included):', utms);
        }
      }
    };
    
    // Push UTMs for debug immediately
    pushUTMsForDebug();
    
    // Also push when location changes (navigation)
    const timeoutId = setTimeout(pushUTMsForDebug, 50);
    return () => clearTimeout(timeoutId);
  }, [location]);

  // ============================================================
  // Track pageview ONLY if analytics consent is granted
  // gclid is included with consent
  // ============================================================
// ============================================================
// Track pageview ONLY if analytics consent is granted
// With URL cleaning to remove sensitive params
// ============================================================
useEffect(() => {
  if (!hasConsent('analytics')) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[GTM] Pageview not tracked - analytics consent not granted');
    }
    return;
  }

  const trackPageView = () => {
    if (window.dataLayer) {
      // ✅ Clean sensitive params from URL
      const cleanUrl = (url) => {
        try {
          const urlObj = new URL(url);
          // Params to remove (sensitive data)
          const sensitiveParams = ['paylinetoken', 'token', 'transaction_id', 'session_id', 'payment_id'];
          sensitiveParams.forEach(param => {
            urlObj.searchParams.delete(param);
          });
          return urlObj.toString();
        } catch (e) {
          return url;
        }
      };
      
      const cleanPath = (url) => {
        try {
          const urlObj = new URL(url);
          const sensitiveParams = ['paylinetoken', 'token', 'transaction_id', 'session_id', 'payment_id'];
          sensitiveParams.forEach(param => {
            urlObj.searchParams.delete(param);
          });
          return urlObj.pathname + urlObj.search;
        } catch (e) {
          return window.location.pathname + window.location.search;
        }
      };
      
      const cleanedLocation = cleanUrl(window.location.href);
      const cleanedPath = cleanPath(window.location.href);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[GTM] Original URL:', window.location.href);
        console.log('[GTM] Cleaned URL:', cleanedLocation);
      }
      
      const pageViewEvent = {
        'event': 'page_view',
        'page_location': cleanedLocation,
        'page_path': cleanedPath,
        'page_title': document.title,
        'page_language': i18n.language || 'en',
      };

      window.dataLayer.push(pageViewEvent);

      if (process.env.NODE_ENV === 'development') {
        console.log('[GTM] Pageview tracked with cleaned URL');
        console.log('[GTM] paylinetoken removed from page_view');
      }
    }
  };

  const timeoutId = setTimeout(trackPageView, 100);
  return () => clearTimeout(timeoutId);
}, [location, hasConsent, i18n.language]);

  return null;
};

// Functional authentication tracking (ALWAYS fires, no consent needed)
export const trackFunctionalAuthEvent = (eventName, eventData = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];
    
    const functionalEvent = {
      'event': eventName,
      'event_type': 'authentication_required',
      'legal_basis': 'GDPR Article 6(1)(b) - contract performance and security',
      'purpose': eventData.purpose || 'security_monitoring_and_authentication',
      'attributes': {
        'route': window.location.pathname || '/',
        'user_type': eventData.user_type || 'regular',
        'login_method': eventData.method || 'form',
        'timestamp': new Date().toISOString(),
        'session_id': getOrCreateSessionId(),
        'is_impersonation': eventData.is_impersonation || false,
        'consent_state': getConsentState()
      }
    };
    
    if (eventData.email) {
      functionalEvent.attributes.email_hash = hashEmail(eventData.email);
    }
    
    if (eventData.reason) {
      functionalEvent.attributes.failure_reason = eventData.reason;
    }
    
    if (eventData.error_type) {
      functionalEvent.attributes.error_type = eventData.error_type;
    }
    
    window.dataLayer.push(functionalEvent);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[GTM-Functional] Authentication event tracked');
    }
    
  } catch (error) {
    console.error('[GTM-Functional] Failed to track functional event:', error);
  }
};

export const gtm = {
  // ============================================================
  // Track custom event (ANALYTICS - requires consent)
  // gclid is included with consent
  // ============================================================
  trackEvent: (eventName, eventParams = {}) => {
    if (!eventName || typeof eventName !== 'string') {
      console.error('[GTM] Invalid event name:', eventName);
      return;
    }

    try {
      const consent = JSON.parse(localStorage.getItem('gtm_consent') || '{"analytics":false}');
      if (!consent.analytics) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Event not tracked - analytics consent not granted');
        }
        return;
      }
      
      if (window.dataLayer) {
        // Get UTMs directly from sessionStorage (gclid included with consent)
        const utms = getUTMsForAnalyticsFromStorage();
        
        window.dataLayer.push({
          'event': eventName,
          ...eventParams,
          'timestamp': new Date().toISOString()
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Event tracked:', eventName);
          console.log('[GTM] GCLID included:', !!utms.gclid);
        }
      }
    } catch (error) {
      console.error('[GTM] Failed to track event:', error);
    }
  },
  
  // ============================================================
  // Track consent granted with UTMs
  // gclid is NEVER included in consent_granted event
  // ============================================================
  trackConsentGranted: (utmParams = {}) => {
    try {
      const consent = JSON.parse(localStorage.getItem('gtm_consent') || '{"analytics":false}');
      if (!consent.analytics) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Consent granted event not tracked - analytics consent not granted');
        }
        return;
      }
      
      if (window.dataLayer) {
        // Filter out empty values and gclid
        const filteredUtms = {};
        Object.keys(utmParams).forEach(key => {
          // gclid is NEVER included in consent_granted event
          if (key === 'gclid') return;
          if (utmParams[key] && utmParams[key] !== '(direct)' && utmParams[key] !== '(none)') {
            filteredUtms[key] = utmParams[key];
          }
        });
        
        window.dataLayer.push({
          'event': 'consent_granted',
          ...filteredUtms,
          'timestamp': new Date().toISOString()
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Consent granted event tracked (gclid NEVER included):', filteredUtms);
        }
      }
    } catch (error) {
      console.error('[GTM] Failed to track consent granted:', error);
    }
  },
  
  // ============================================================
  // Track registration - gclid included with consent
  // ============================================================
  trackRegistration: (method = 'registration_form', additionalParams = {}) => {
    try {
      const consent = JSON.parse(localStorage.getItem('gtm_consent') || '{"analytics":false}');
      if (!consent.analytics) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Registration not tracked - analytics consent not granted');
        }
        return;
      }
      
      if (window.dataLayer) {
        const utms = getUTMsForAnalyticsFromStorage();
        
        window.dataLayer.push({
          'event': 'sign_up',
          'method': method,
          ...additionalParams,
          ...utms,
          'timestamp': new Date().toISOString()
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Registration tracked:', method);
          console.log('[GTM] GCLID included:', !!utms.gclid);
        }
      }
    } catch (error) {
      console.error('[GTM] Failed to track registration:', error);
    }
  },
  
  // ============================================================
  // Track login - gclid included with consent
  // ============================================================
  trackLogin: (method = 'login_form', additionalParams = {}) => {
    try {
      const consent = JSON.parse(localStorage.getItem('gtm_consent') || '{"analytics":false}');
      if (!consent.analytics) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Login analytics not tracked - consent not granted');
        }
        return;
      }
      
      if (window.dataLayer) {
        const utms = getUTMsForAnalyticsFromStorage();
        
        window.dataLayer.push({
          'event': 'login_analytics',
          'method': method,
          ...additionalParams,
          ...utms,
          'timestamp': new Date().toISOString()
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Login analytics tracked:', method);
          console.log('[GTM] GCLID included:', !!utms.gclid);
        }
      }
    } catch (error) {
      console.error('[GTM] Failed to track login analytics:', error);
    }
  },
  
  // ============================================================
  // Track purchase - gclid included with consent
  // ============================================================
// components/GTM.js - Update the trackPurchase method


  // ============================================================
  // Track purchase - gclid included with consent
  // ============================================================
  trackPurchase: (purchaseData = {}) => {
    if (!purchaseData || Object.keys(purchaseData).length === 0) {
      console.warn('[GTM] Purchase tracking called with empty data');
      return null;
    }

    try {
      const consent = JSON.parse(localStorage.getItem('gtm_consent') || '{"analytics":false}');
      const hasAnalyticsConsent = consent.analytics === true;

      if (window.dataLayer) {
        // ⭐ EXTRACT payment_status and user_data specifically
        const {
          utm_source, utm_medium, utm_campaign, utm_content, utm_term, utm_id, gclid,
          user_data,
          payment_status,
          payline_response,
          ...ecommerceData
        } = purchaseData;

        const ecommerceObject = {
          transaction_id: ecommerceData.transaction_id || `TXN_${Date.now()}`,
          value: ecommerceData.value || 0,
          tax: ecommerceData.tax || 0,
          shipping: ecommerceData.shipping || 0,
          currency: ecommerceData.currency || 'EUR',
          coupon: ecommerceData.coupon || undefined,
          items: ecommerceData.items || []
        };

        const currentEventId = `${ecommerceObject.transaction_id}_${Math.floor(Date.now() / 1000)}`;
        const now = Date.now();

        // Duplicate check (module-level)
        if (purchaseEventId === currentEventId && (now - purchaseEventTimestamp) < 5000) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[GTM] Duplicate purchase event detected, skipping');
          }
          return null;
        }

        // Duplicate check (sessionStorage)
        const lastEventId = sessionStorage.getItem('last_purchase_event_id');
        const lastEventTime = parseInt(sessionStorage.getItem('last_purchase_event_time') || '0', 10);

        if (lastEventId === currentEventId && (now - lastEventTime) < 5000) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[GTM] Duplicate purchase event detected, skipping');
          }
          return null;
        }

        purchaseEventId = currentEventId;
        purchaseEventTimestamp = now;

        sessionStorage.setItem('last_purchase_event_id', currentEventId);
        sessionStorage.setItem('last_purchase_event_time', now.toString());

        // Get UTMs directly from sessionStorage (gclid included with consent)
        const utmsFromStorage = getUTMsForAnalyticsFromStorage();

        const dataLayerEvent = {
          event: 'purchase',
          ecommerce: ecommerceObject,
          page_location: window.location.href,
          timestamp: new Date().toISOString(),
          event_id: currentEventId,
          consent_given: hasAnalyticsConsent,
          data_limited: !hasAnalyticsConsent,
        };

        // ⭐ ADD payment_status (ALWAYS included)
        if (payment_status) {
          dataLayerEvent.payment_status = payment_status;
        } else {
          dataLayerEvent.payment_status = 'unknown';
        }

        // ⭐ Optionally include payline_response for debugging (dev only)
        if (payline_response && process.env.NODE_ENV === 'development') {
          dataLayerEvent.payline_response = payline_response;
        }

        // ⬇️ ADD ENHANCED CONVERSIONS DATA
        if (user_data && typeof user_data === 'object') {
          const enhancedUserData = {};

          if (user_data.email) enhancedUserData.email = user_data.email;
          if (user_data.phone_number) enhancedUserData.phone_number = user_data.phone_number;

          if (user_data.address && typeof user_data.address === 'object') {
            const address = {};
            if (user_data.address.first_name) address.first_name = user_data.address.first_name;
            if (user_data.address.last_name) address.last_name = user_data.address.last_name;
            if (user_data.address.street) address.street = user_data.address.street;
            if (user_data.address.city) address.city = user_data.address.city;
            if (user_data.address.postal_code) address.postal_code = user_data.address.postal_code;
            if (user_data.address.region) address.region = user_data.address.region;
            if (user_data.address.country) address.country = user_data.address.country;

            if (Object.keys(address).length > 0) {
              enhancedUserData.address = address;
            }
          }

          if (Object.keys(enhancedUserData).length > 0) {
            dataLayerEvent.user_data = enhancedUserData;
          }
        }

        // ============================================================
        // ✅ FIX: UTMs and gclid are now RE-ATTACHED to top-level
        // ============================================================
        if (hasAnalyticsConsent) {
          dataLayerEvent.is_primary_purchase = true;

          // Re-attach UTMs to top-level (they were destructured out above)
          // Prefer values passed from PaymentSuccess, fall back to sessionStorage
          const finalUtms = {
            utm_source:   utm_source   || utmsFromStorage.utm_source,
            utm_medium:   utm_medium   || utmsFromStorage.utm_medium,
            utm_campaign: utm_campaign || utmsFromStorage.utm_campaign,
            utm_content:  utm_content  || utmsFromStorage.utm_content,
            utm_term:     utm_term     || utmsFromStorage.utm_term,
            utm_id:       utm_id       || utmsFromStorage.utm_id,
          };

          Object.entries(finalUtms).forEach(([key, value]) => {
            if (value && value !== '' && value !== '(direct)' && value !== '(none)') {
              dataLayerEvent[key] = value;
            }
          });

          // ⭐ gclid only with consent
          const gclidValue = gclid || utmsFromStorage.gclid;
          if (gclidValue && gclidValue !== '') {
            dataLayerEvent.gclid = gclidValue;
          }

        } else {
          dataLayerEvent.is_essential_only = true;
          dataLayerEvent.reason = 'analytics_consent_not_granted';
          // ❌ NO UTMs, NO gclid when consent is denied
        }

        window.dataLayer.push(dataLayerEvent);

        if (process.env.NODE_ENV === 'development') {
          console.log('[GTM] Purchase tracked:', {
            transaction_id: ecommerceObject.transaction_id,
            consent_given: hasAnalyticsConsent,
            payment_status: dataLayerEvent.payment_status,
            includes_gclid: !!dataLayerEvent.gclid,
            includes_utms: !!(dataLayerEvent.utm_source || dataLayerEvent.utm_medium),
            includes_user_data: !!dataLayerEvent.user_data,
            user_data_fields: dataLayerEvent.user_data ? Object.keys(dataLayerEvent.user_data) : []
          });
        }
      }

      return purchaseData;
    } catch (error) {
      console.error('[GTM] Failed to track purchase:', error);
      return null;
    }
  },
  
  // Get current consent state
  getConsent: () => {
    try {
      const consent = localStorage.getItem('gtm_consent');
      return consent ? JSON.parse(consent) : null;
    } catch {
      return null;
    }
  },
  
  // Clear all tracking data
  clear: () => {
    try {
      localStorage.removeItem('gtm_consent');
      sessionStorage.removeItem('last_purchase_event_id');
      sessionStorage.removeItem('last_purchase_event_time');
      sessionStorage.removeItem('gtm_functional_session_id');
      purchaseEventId = null;
      purchaseEventTimestamp = 0;
      
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'clear_data',
          'timestamp': new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('[GTM] Failed to clear data:', error);
    }
  },
  
  // Get GTM status
  getStatus: () => {
    try {
      return {
        connected: typeof window.gtag === 'function',
        gtag: typeof window.gtag,
        dataLayer: Array.isArray(window.dataLayer),
        dataLayerLength: window.dataLayer?.length || 0,
        consent: JSON.parse(localStorage.getItem('gtm_consent') || '{}'),
        lastPurchaseEventId: purchaseEventId,
        lastPurchaseEventTime: purchaseEventTimestamp,
        functionalSessionId: sessionStorage.getItem('gtm_functional_session_id'),
        utmsFromStorage: getUTMsFromSessionStorage()
      };
    } catch {
      return {
        connected: false,
        gtag: 'undefined',
        dataLayer: false,
        dataLayerLength: 0,
        consent: {}
      };
    }
  },
  
  // Quick consent check helper
  hasConsent: (type) => {
    try {
      const consent = JSON.parse(localStorage.getItem('gtm_consent') || '{}');
      return consent[type] === true;
    } catch {
      return false;
    }
  },
  
  // Functional tracking method
  trackFunctionalEvent: trackFunctionalAuthEvent
};

export default GTM;